import React, { useState } from 'react';
import { MessageCircle, X, Send, ArrowRight } from 'lucide-react';
import { trackWhatsAppClick } from '../utils/tracking';

const WhatsAppChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const phoneNumber = '918679333354';

  const quickReplies = [
    { text: 'Get Wedding Quote', emoji: '💍', message: 'Hi! I would like to get a wedding quote for my upcoming wedding.' },
    { text: 'Check Date Availability', emoji: '📅', message: 'Hi! I want to check venue availability for my wedding date.' },
    { text: 'Talk to Planner', emoji: '👩‍💼', message: 'Hi! I would like to speak with a wedding planner about my requirements.' },
    { text: 'View Packages', emoji: '📦', message: 'Hi! Please share your wedding packages and pricing details.' },
  ];

  const handleQuickReply = (message) => {
    trackWhatsAppClick();
    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  return (
    <>
      {/* Chat Widget Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-24 right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 hover:scale-110 ${
          isOpen ? 'bg-gray-600' : ''
        }`}
        style={!isOpen ? { 
          background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
          boxShadow: '0 8px 25px rgba(37, 211, 102, 0.4)'
        } : {}}
        data-testid="whatsapp-chat-widget"
        aria-label={isOpen ? 'Close WhatsApp chat' : 'Open WhatsApp chat'}
        aria-expanded={isOpen}
        title="Chat on WhatsApp"
      >
        {isOpen ? (
          <X className="h-6 w-6 text-white" aria-hidden="true" />
        ) : (
          <MessageCircle className="h-7 w-7 text-white" aria-hidden="true" />
        )}
      </button>

      {/* Notification Badge */}
      {!isOpen && (
        <span className="fixed bottom-[118px] right-5 z-50 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold animate-pulse" aria-label="1 new message">
          1
        </span>
      )}

      {/* Chat Window - Responsive for mobile */}
      {isOpen && (
        <div 
          className="fixed bottom-40 right-2 sm:right-4 z-50 w-[calc(100vw-16px)] sm:w-80 max-w-80 bg-white rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-5 duration-300"
          style={{ boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)' }}
        >
          {/* Header */}
          <div 
            className="p-4 text-white"
            style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <MessageCircle className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold">Wedding Support</h3>
                <p className="text-sm text-white/80 flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-300 rounded-full"></span>
                  Online • Replies instantly
                </p>
              </div>
            </div>
          </div>

          {/* Chat Content */}
          <div className="p-4 bg-[#E5DDD5]">
            {/* Welcome Message Bubble */}
            <div className="bg-white rounded-lg rounded-tl-none p-3 shadow-sm max-w-[85%] mb-4">
              <p className="text-sm text-gray-700">
                👋 Hi! Welcome to Destination Weddings & Events. How can we help you plan your dream wedding?
              </p>
              <p className="text-[10px] text-gray-400 text-right mt-1">Just now</p>
            </div>

            {/* Quick Reply Buttons */}
            <div className="space-y-2">
              {quickReplies.map((reply, index) => (
                <button
                  key={index}
                  onClick={() => handleQuickReply(reply.message)}
                  className="w-full bg-white hover:bg-gray-50 text-left p-3 rounded-lg shadow-sm transition-all duration-200 hover:shadow-md flex items-center justify-between group"
                >
                  <span className="flex items-center gap-2">
                    <span>{reply.emoji}</span>
                    <span className="text-sm text-gray-700">{reply.text}</span>
                  </span>
                  <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-[#25D366] group-hover:translate-x-1 transition-all" />
                </button>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="p-3 bg-white border-t border-gray-100">
            <button
              onClick={() => handleQuickReply('Hi! I have a question about wedding planning.')}
              className="w-full py-3 rounded-xl text-white font-semibold flex items-center justify-center gap-2 transition-all hover:opacity-90"
              style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}
            >
              <Send className="h-4 w-4" />
              Start Chat
            </button>
            <p className="text-center text-[10px] text-gray-400 mt-2">
              Powered by WhatsApp
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default React.memo(WhatsAppChatWidget);
